<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import {shownDescription} from '@/utils/markdownMentions'
import ViewInfo from '../../shared/ViewInfo.vue'
import WorkspaceClosureDialog from '@/components/shared/WorkspaceClosureDialog.vue'
import AskDialog, {deleteActions} from '@/components/shared/AskDialog.vue'
import InfoRow from '@/components/shared/InfoRow.vue'
import ClassDiagram from '@/components/design/system/ClassDiagram.vue'
import DesignDialogs from '@/components/design/system/DesignDialogs.vue'
import ClassDialog from '@/components/design/class/ClassDialog.vue'
import WorkflowDialog from '@/components/design/workflow/WorkflowDialog.vue'
import {DependencyError, NotUniqueError} from "@/errors/errors";
import {saveErrorMessage} from "@/utils/saveErrors";
import {refuse, showRefusal} from "@/utils/refusal";
import Idea from "@/types/Idea";
import WorkspaceRouteComponent from "@/components/shared/WorkspaceRouteComponent";
import {DiagramTarget} from "@/utils/classModel";
import {workspaceDesign} from "@/utils/heading";
import {PropertySave} from "@/types/PropertySave";
import Workflow from "@/types/Workflow";
import Domain from "@/types/Domain";
import DomainDialog from "@/components/design/domain/DomainDialog.vue";
import RolesDialog from "@/components/design/system/RolesDialog.vue";
import {coloredRoles, WorkspaceRole} from "@/types/Workspace";
import ColoredEntryList from "@/components/shared/ColoredEntryList.vue";
import StateList from "@/components/design/workflow/StateList.vue";
import {citingFormulasMessage, domainErrorMessage} from "@/utils/domainErrors";
import {timeZoneOf} from "@/utils/timeZones";
import Section from "@/components/shared/Section.vue";
import MarkdownExcerpt from "@/components/shared/markdown/MarkdownExcerpt.vue";
import MarkdownViewer from "@/components/shared/markdown/MarkdownViewer.vue";
import {isSingleLineMarkdown} from "@/utils/markdownExcerpt";
import {ViewTab} from "@/components/shared/ViewTabs.vue";
import TabbedSection from "@/components/shared/TabbedSection.vue";
import MembersPanel from "@/components/access/MembersPanel.vue";
import {DESIGN_OPENS, DESIGN_TABS} from "@/utils/breadcrumb";

/**
 * O rótulo e o ícone de cada aba (ícone pedido pelo usuário). Os rótulos são as chaves que já nomeavam
 * as seções, mais a das informações e a dos membros; os fluxos com o nome curto, só aqui: aba pede
 * palavra curta, e "Fluxos de trabalho" continua sendo o nome (pedido do usuário).
 *
 * Os ícones são os que o app já usa para cada coisa — a coleção e o domínio como no diagrama, o fluxo
 * como no compartimento dele na caixa da coleção —, para a aba e o desenho falarem a mesma língua.
 */
const TABS = {
  info: {label: 'info-tab', icon: 'mdi-information-outline'},
  collections: {label: 'collections', icon: 'mdi-table'},
  workflows: {label: 'workflows-short', icon: 'mdi-sync'},
  domains: {label: 'domains', icon: 'mdi-format-list-bulleted'},
  roles: {label: 'roles', icon: 'mdi-badge-account-outline'},
  members: {label: 'members-tab', icon: 'mdi-account-multiple'}
} as const

@Component({
  components: {
    'ontos-workspace-closure-dialog': WorkspaceClosureDialog,
    'ontos-ask-dialog': AskDialog,
    'ontos-view-info': ViewInfo,
    'ontos-info-row': InfoRow,
    'ontos-class-diagram': ClassDiagram,
    'ontos-design-dialogs': DesignDialogs,
    'ontos-class-dialog': ClassDialog,
    'ontos-workflow-dialog': WorkflowDialog,
    'ontos-domain-dialog': DomainDialog,
    'ontos-roles-dialog': RolesDialog,
    'ontos-colored-entry-list': ColoredEntryList,
    'ontos-state-list': StateList,
    'ontos-tabbed-section': TabbedSection,
    'ontos-members-panel': MembersPanel,
    'ontos-section': Section,
    'ontos-markdown-excerpt': MarkdownExcerpt,
    'markdown-viewer': MarkdownViewer
  }
})
class SystemDesign extends WorkspaceRouteComponent {
  //Exposto ao template: o fuso que vale, com o padrão no ambiente que não escolheu um
  timeZoneOf = timeZoneOf

  /** A descrição como se mostra, com as referências desenhadas — ver `shownDescription`. */
  get description(): string {
    return shownDescription(this.workspace, this.workspace)
  }

  /** A descrição cabe como campo? A mesma regra do markdown na ficha. */
  get descriptionInline(): boolean {
    return isSingleLineMarkdown(this.description)
  }

  /**
   * As partes da tela em abas, e não empilhadas (pedido do usuário: a tela era longa e exigia muita
   * rolagem).
   *
   * Membros só para o administrador (pedido do usuário). `is`, e não `can`: num ambiente encerrado a
   * lista continua sendo dele para ler; mexer nela o painel pergunta por conta própria.
   */
  get tabs(): ViewTab[] {
    return DESIGN_TABS
        .filter(value => value !== 'members' || this.is('admin'))
        .map(value => ({value, icon: TABS[value].icon, title: this.$tt(TABS[value].label) as string}))
  }

  tabOpens = DESIGN_OPENS

  get roles(): WorkspaceRole[] {
    return coloredRoles(this.workspace.roles || [])
  }
  /** O que foi clicado no diagrama, ou null com tudo fechado */
  onDesign: DiagramTarget | null = null
  creatingIdea = false
  creatingWorkflow = false
  creatingDomain = false
  editingDomain: Domain | null = null
  editingRoles = false
  /** O fluxo aberto para design pelo card da seção, ou null */
  editingWorkflow: Workflow | null = null
  savingDesign = false
  expanded = false

  get heading() {
    return workspaceDesign(this.workspace)
  }

  async load() {}

  /**
   * O alvo do clique é o mesmo estado nos dois diagramas — o da página e o de tela cheia.
   * São duas instâncias do componente, mas uma coisa só aberta por vez.
   */
  /**
   * O que foi clicado no diagrama abre o modal daquele alvo.
   *
   * Sem caso especial para o ambiente encerrado nem para o nível, e não por esquecimento: o
   * alvo de COLEÇÃO já abre o `ViewClassDialog`, que é a visualização e não um editor — e o
   * `ViewClass` lá dentro esconde os próprios botões de alteração pelo mesmo `can` de sempre.
   * Quem não tem o que mostrar em leitura — propriedade, fluxo, domínio — o diagrama nem deixa
   * passar.
   */
  open(target: DiagramTarget) {
    this.onDesign = target
  }

  /**
   * As ligações dos modais, uma vez só.
   *
   * Eles são montados em dois lugares — dentro da tela cheia e fora dela —, e repetir
   * seis props e cinco eventos nos dois pontos era o caminho para os dois se soltarem um
   * do outro na primeira mudança.
   */
  get designDialogs() {
    return {workspace: this.workspace, target: this.onDesign, saving: this.savingDesign}
  }

  get designDialogListeners() {
    return {
      closed: this.closeDesign,
      'save-property': this.updateProperty,
      'save-workflow': this.saveWorkflow,
      'save-domain': this.saveDomain,
      showMessage: (message: any) => this.$emit('showMessage', message),
      showErrorMessage: (message: any) => this.$emit('showErrorMessage', message)
    }
  }

  closeDesign() {
    this.onDesign = null
  }

  /**
   * Salva e fecha; deixa aberto se der errado.
   *
   * É o padrão do ViewClass: com o modal na tela, o erro pede correção ali mesmo, então a janela de
   * recusa (`showRefusal`) em vez de `throwError`, e o fechamento só no sucesso.
   *
   * `onError` pode ser função do erro, para quando a mensagem depende dele — o domínio
   * diz quantos atributos ou itens impediram a gravação.
   */
  private async persist(action: () => Promise<void>, onError: string | ((err: unknown) => string),
                        close: () => void = this.closeDesign) {
    this.savingDesign = true
    try {
      await action()
      close()
    } catch (err) {
      console.error(err)
      showRefusal(typeof onError === 'function' ? onError(err) : onError)
    } finally {
      this.savingDesign = false
    }
  }

  closeWorkflowDialog() {
    this.creatingWorkflow = false
    this.editingWorkflow = null
  }

  closeDomainDialog() {
    this.creatingDomain = false
    this.editingDomain = null
  }

  /**
   * O Salvar da janela de papéis: a lista inteira. As recusas que ela tem: o papel excluído que uma
   * fórmula cita (com quais), e o identificador ou nome repetido — que a janela já confere, e o
   * servidor de novo. Recusada, a janela fica aberta com o rascunho, para consertar.
   */
  async saveRolesFromDesign(roles: WorkspaceRole[]) {
    await this.persist(
        async () => {
          await this.saveRoles(roles)
          this.info(this.$tt('info.roles-updated'))
        },
        err => citingFormulasMessage(err)
            || (err instanceof NotUniqueError ? this.$tt('role-repeated') as string : this.$tt('error.update-roles') as string),
        () => this.editingRoles = false)
  }

  async createDomainFromDesign(domain: Domain) {
    await this.persist(
        async () => {
          await this.createDomain(domain)
          this.info(this.$tt('info.domain-created', {name: domain.name}))
        },
        this.$tt('error.create-domain', {name: domain.name}) as string,
        this.closeDomainDialog)
  }

  /**
   * Os dois caminhos que chegam ao design de um domínio — a caixa dele no diagrama e o
   * card da seção — só diferem em ONDE está o domínio e no que fechar depois. É o mesmo
   * par do fluxo, e o meio é um só.
   */
  private updateDomainAndClose(domain: Domain, changes: any, close: () => void) {
    return this.persist(
        async () => {
          await this.updateDomain(domain, changes)
          this.info(this.$tt('info.domain-updated', {name: domain.name}))
        },
        err => domainErrorMessage(err, this.$tt('error.update-domain', {name: domain.name})),
        close)
  }

  /** Veio do clique no diagrama: o domínio é o da caixa apontada */
  async saveDomain(changes: any) {
    const alias = (this.onDesign as { domain: string }).domain
    const domain = this.workspace.domains.find(candidate => candidate.alias === alias)!
    await this.updateDomainAndClose(domain, changes, this.closeDesign)
  }

  /** Veio do card da seção: o domínio é o que abriu o modal */
  async saveEditedDomain(changes: any) {
    await this.updateDomainAndClose(this.editingDomain!, changes, this.closeDomainDialog)
  }

  async updateProperty(save: PropertySave) {
    const idea = this.targetIdea
    await this.persist(
        async () => {
          await this.saveProperty(save)
          this.info(this.$tt('info.property-updated', {property: save.name, collection: idea.name}))
        },
        //A fórmula recusada tem frase própria; o genérico fica para o que não tem
        err => saveErrorMessage(err, idea) || this.$tt('error.update-property', {name: save.name}) as string)
  }

  /**
   * Fecha e deixa a coleção nova aparecer no diagrama, em vez de navegar para a ficha
   * dela como a página faz: sair da tela é justamente o que o modal veio evitar.
   *
   * Nome repetido volta para o campo que colidiu, e não para a barra de mensagens — o
   * formulário está na tela, e é lá que se corrige.
   */
  async createCollection(idea: Idea) {
    this.savingDesign = true
    try {
      await this.createIdea(idea)
      this.info(this.$tt('info.collection-created', {name: idea.name}))
      this.creatingIdea = false
    } catch (err) {
      if (err instanceof NotUniqueError) {
        (this.$refs.classDialog as any).notUnique(err.data.property)
      } else {
        refuse(err, idea, {fallback: this.$tt('error.create-collection', {name: idea.name})})
      }
    } finally {
      this.savingDesign = false
    }
  }

  /**
   * Fecha e deixa o fluxo novo aparecer na seção, em vez de navegar para a ficha dele
   * como a página de criar faz — o mesmo raciocínio de createCollection.
   */
  async createWorkflowFromDesign(workflow: Workflow) {
    await this.persist(
        async () => {
          await this.createWorkflow(workflow)
          this.info(this.$tt('info.workflow-created', {name: workflow.name}))
        },
        this.$tt('error.create-workflow', {name: workflow.name}) as string,
        this.closeWorkflowDialog)
  }

  /**
   * Os dois caminhos que chegam ao design de um fluxo — o compartimento de fluxo de uma
   * classe no diagrama e o card da seção — só diferem em ONDE está o fluxo e no que
   * fechar depois. O meio é o mesmo, então mora aqui.
   */
  private updateWorkflowAndClose(workflow: Workflow, changes: any, close: () => void) {
    return this.persist(
        async () => {
          await this.updateWorkflow(workflow, changes)
          this.info(this.$tt('info.workflow-updated', {name: workflow.name}))
        },
        //Situação removida que alguma fórmula cita tem frase própria; o genérico fica para o resto
        err => citingFormulasMessage(err) || this.$tt('error.update-workflow', {name: workflow.name}) as string,
        close)
  }

  /**
   * A coleção do alvo aberto. Todo alvo tem uma, menos o do domínio — e nenhum dos que
   * chegam aqui é dele: o modal do domínio grava pelo saveDomain.
   */
  private get targetIdea(): Idea {
    return this.workspace.ideaWith((this.onDesign as { idea: string }).idea)!
  }

  /** Veio do clique no diagrama: o fluxo é o da classe apontada */
  async saveWorkflow(changes: any) {
    const workflow = this.workspace.workflowOf(this.targetIdea) as Workflow
    await this.updateWorkflowAndClose(workflow, changes, this.closeDesign)
  }

  /** Veio do card da seção: o fluxo é o que abriu o modal */
  async saveEditedWorkflow(changes: any) {
    await this.updateWorkflowAndClose(this.editingWorkflow!, changes, this.closeWorkflowDialog)
  }

  /** Qual pergunta está aberta. Uma de cada vez, e por isso um campo só em vez de dois. */
  asking: 'closure' | 'delete' | null = null

  /** Confirmada, a pergunta vira o inverso do estado de agora — ver o `closing` do diálogo. */
  async onClosureConfirmed() {
    const closing = this.can('admin')
    this.asking = null
    await this.setWorkspaceClosed(closing)
  }

  //Exposta ao template: as saídas de toda exclusão
  deleteActions = deleteActions

  async onDeletePicked(action: string | null) {
    this.asking = null
    if (action === 'delete') await this.deleteSystem()
  }

  async deleteSystem() {
    try {
      await this.saving(async () => {
        const {system: systemAlias} = this.params
        await this.$systems.delete({systemAlias})
        this.info(this.$tt('info.workspace-deleted', {name: this.workspace.title}))
        await this.$router.push({name: 'home'})
      })
    } catch (err) {
      if(err instanceof DependencyError) {
        const count = err.data.dependencies.idea
        //$tc e não $tt: a frase tem singular e plural
        this.throwError(this.$tc('error.delete-workspace-collections', count, {count}))
      }
      this.throwError(this.$tt('error.delete-workspace', {name: this.workspace.title}), err)
    }
  }
}

export default toNative(SystemDesign)
</script>
<template>
  <ontos-base v-bind="baseProps" ref="base">
    <div v-if="loaded">
      <ontos-page-header v-bind="heading" :level="1">
        <!-- As duas são do ADMINISTRADOR: o ambiente é dele, o design é do designer. Um
             designer desenha aqui dentro e não encerra nem exclui o ambiente — e, não podendo,
             não vê os botões. Ficam no cabeçalho da página porque agem sobre o ambiente INTEIRO;
             o Editar, que muda só o que a aba Informações mostra, mora nela (pedido do usuário).

             Encerrado, só o reabrir fica: excluir é alteração, e num ambiente congelado não é
             desenhado. O reabrir mora aqui porque é daqui que se encerrou —
             e esta tela continua acessível em leitura, então o caminho de volta existe. Ele
             pergunta `is` e não `can`, porque num ambiente encerrado o `can` é falso para todos:
             reabrir é a única escrita que o portão aceita. -->
        <template #actions>
          <template v-if="can('admin')">
            <ontos-header-action icon="mdi-archive-arrow-down-outline" :label="$tt('workspace-close')"
                                 @click="asking = 'closure'"/>
            <ontos-header-action icon="mdi-delete" :label="$tt('delete')"
                                 color="error" @click="asking = 'delete'"/>
          </template>
          <ontos-header-action v-else-if="is('admin')" icon="mdi-archive-arrow-up-outline"
                               :label="$tt('workspace-reopen')" color="primary"
                               @click="asking = 'closure'"/>
        </template>
      </ontos-page-header>

      <!-- Os dois sentidos mudam o ambiente INTEIRO de uma vez, e os dois dizem antes o que
           vai acontecer. Qual das duas perguntas é esta sai do próprio ambiente. -->
      <ontos-workspace-closure-dialog v-if="asking === 'closure'" :closing="can('admin')"
                                      :name="workspace.title"
                                      @confirmed="onClosureConfirmed()"
                                      @cancelled="asking = null"/>

      <!-- Excluir é a única das três que não tem volta, e a pergunta diz as duas coisas que o
           usuário precisa saber antes: que não dá para desfazer, e que ENCERRAR existe e faz o
           que ele talvez queira. Direto no AskDialog, que é o chassi de toda pergunta daqui —
           componente próprio só se justificaria se fossem duas telas, como no encerramento. -->
      <ontos-ask-dialog v-if="asking === 'delete'"
                        :title="$tt('workspace-delete-confirm', {name: workspace.title})"
                        :message="$tt('workspace-delete-explain')"
                        :actions="deleteActions()"
                        @pick="onDeletePicked($event)"
                        @closed="asking = null"/>

      <!-- As partes do ambiente em abas, e não empilhadas: a tela era longa e exigia muita rolagem
           (pedido do usuário). O arranjo — abas, botão principal, telefone, URL — é o do
           TabbedSection, o mesmo do design da coleção.

           O conteúdo é montado por `v-if`, e não escondido: o diagrama mede a própria largura ao
           nascer para se enquadrar, e escondido ele mediria zero. -->
      <ontos-tabbed-section :tabs="tabs" :opens="tabOpens" routed>
        <!-- Criar e editar de cada aba: o que se FAZ na aba. Os controles do diagrama moram nele.

             Todos abrem janela, e não navegação: criar ou editar não precisa tirar ninguém do
             modelo que está lendo. As rotas continuam existindo e viram o "abrir em página
             inteira" de dentro das janelas.

             Os três "+" desta tela e o "Design" dos cards de fluxo e domínio ficaram sem
             `v-if` quando o encerramento entrou: num ambiente congelado eles abriam o editor
             e só o servidor recusava no fim. Agora todos perguntam a mesma coisa. -->
        <template #controls="{tab}">
          <!-- Renomear o ambiente é do ADMINISTRADOR, e num ambiente encerrado não se desenha -->
          <ontos-header-action v-if="tab === 'info' && can('admin')" icon="mdi-pencil" color="primary"
                               :label="$tt('edit')" :to="{name: 'edit-system', params: {system: params.system}}"/>
          <ontos-header-action v-else-if="tab === 'collections' && can('designer')" icon="mdi-plus"
                               color="primary" :label="$tt('collection-create')" @click="creatingIdea = true"/>
          <ontos-header-action v-else-if="tab === 'workflows' && can('designer')" icon="mdi-plus"
                               color="primary" :label="$tt('workflow-create-short')" @click="creatingWorkflow = true"/>
          <ontos-header-action v-else-if="tab === 'domains' && can('designer')" icon="mdi-plus"
                               color="primary" :label="$tt('domain-create')" @click="creatingDomain = true"/>
          <!-- Os papéis: aqui só a lista; editar é na janela, com a mesma lista editável -->
          <ontos-header-action v-else-if="tab === 'roles' && can('designer')" icon="mdi-pencil"
                               color="primary" :label="$tt('roles-edit')" @click="editingRoles = true"/>
        </template>

        <template #default="{tab}">
        <!-- A descrição pela regra da ficha (ver isSingleLineMarkdown): de uma linha, um campo como os
             outros; de mais linhas, uma seção própria logo abaixo, com o texto sem borda. Não
             aparecia aqui — só se via editando. -->
        <template v-if="tab === 'info'">
          <ontos-info-row>
            <ontos-view-info :title="$tt('workspace-name')">{{ workspace.title }}</ontos-view-info>
            <ontos-view-info :title="$tt('alias')"><span class="ontos-code">{{ workspace.alias }}</span></ontos-view-info>
            <ontos-view-info :title="$tt('time-zone')">{{ timeZoneOf(workspace) }}</ontos-view-info>
            <ontos-view-info v-if="description && descriptionInline" :title="$tt('workspace-description')">
              <ontos-markdown-excerpt :value="description" full/>
            </ontos-view-info>
          </ontos-info-row>
          <ontos-section v-if="description && !descriptionInline" :title="$tt('workspace-description')"
                         :level="3" dense>
            <markdown-viewer :initialValue="description" class="ontos-inset mb-3"/>
          </ontos-section>
        </template>

        <template v-else-if="tab === 'collections'">
          <ontos-class-diagram v-if="workspace.ideas.length" :workspace="workspace" expandable
                               @open="open($event)" @expand="expanded = true"/>
          <div v-else class="text-body-2 text-grey">{{ $tt('collections-empty') }}</div>
        </template>

        <!-- Um bloco por fluxo, como os papéis (decisão do usuário): nome e alias no título,
             "Editar", e as situações só leitura, com o nome de cada coluna.

             O "Editar" abre o modal: desenhar um fluxo não precisa tirar ninguém da tela, e a
             ficha continua acessível pelo "abrir em página inteira". Sem o nível, o botão não
             aparece — as situações já estão à vista no próprio bloco. -->
        <div v-else-if="tab === 'workflows'">
          <ontos-state-list v-for="(workflow, index) of workspace.workflows" :key="workflow.alias"
                            :states="workflow.states" :title="workflow.name" :level="3" dense
                            :divider="index > 0">
            <template #subtitle><span class="ontos-code">{{ workflow.alias }}</span></template>
            <template #actions>
              <ontos-header-action v-if="can('designer')" icon="mdi-pencil" :label="$tt('edit')"
                                   @click="editingWorkflow = workflow"/>
            </template>
          </ontos-state-list>
          <div v-if="!workspace.workflows.length" class="text-body-2 text-grey">
            {{ $tt('workflows-empty') }}
          </div>
        </div>

        <!-- O mesmo bloco do fluxo, com a opção no chip -->
        <div v-else-if="tab === 'domains'">
          <ontos-colored-entry-list v-for="(domain, index) of workspace.domains" :key="domain.alias"
                                    :entries="domain.options" :title="domain.name" :level="3" dense
                                    :divider="index > 0">
            <template #subtitle><span class="ontos-code">{{ domain.alias }}</span></template>
            <template #actions>
              <ontos-header-action v-if="can('designer')" icon="mdi-pencil" :label="$tt('edit')"
                                   @click="editingDomain = domain"/>
            </template>
          </ontos-colored-entry-list>
          <div v-if="!workspace.domains.length" class="text-body-2 text-grey">
            {{ $tt('domains-empty') }}
          </div>
        </div>

        <!-- Sem cabeçalho próprio: o nome é o da aba, e o Editar está na linha dela -->
        <div v-else-if="tab === 'roles'">
          <ontos-colored-entry-list :entries="roles" :header="false" :divider="false"
                                    :empty-text="$tt('roles-empty')"/>
        </div>

        <!-- Quem participa do ambiente: era uma tela, o modo de acesso; é esta aba (pedido do
             usuário). O painel carrega a lista ao montar e avisa como uma tela, e os avisos sobem
             daqui para a barra de mensagens. -->
        <ontos-members-panel v-else :workspace="workspace"
                             @showMessage="$emit('showMessage', $event)"
                             @showErrorMessage="$emit('showErrorMessage', $event)"/>
        </template>
      </ontos-tabbed-section>

      <!-- O diagrama em tela cheia é o MESMO componente, não uma cópia: um diagrama
           numa faixa de 60vh fica apertado assim que passam de meia dúzia de coleções. -->
      <v-dialog v-model="expanded" fullscreen>
        <!-- Título e corpo nos v-card-title/v-card-text, como todo modal da casa: são
             eles que trazem o respiro das bordas. Com os filhos soltos sob um pa-4, o
             conteúdo ficava colado nos cantos. -->
        <v-card class="expanded-card">
          <v-card-title>
            <ontos-page-header :title="$tt('collections')" :level="2">
              <!-- O X padrão dos modais, e não um botão próprio: é o mesmo componente que
                   fecha a ficha, o Kanban e o design da coleção. Sem rota de página
                   inteira, porque o diagrama expandido é a mesma tela de trás. Os controles do
                   zoom estão no canto do próprio diagrama. -->
              <template #controls>
                <ontos-dialog-actions @closed="expanded = false"/>
              </template>
            </ontos-page-header>
          </v-card-title>
          <v-card-text class="expanded-body">
            <ontos-class-diagram v-if="expanded" :workspace="workspace"
                                 class="expanded-diagram" @open="open($event)"/>
          </v-card-text>

          <!-- DENTRO do diálogo: um modal aberto daqui precisa nascer na subárvore deste
               v-dialog, senão o ESC fecha a tela cheia em vez dele. -->
          <ontos-design-dialogs v-if="expanded" v-bind="designDialogs" v-on="designDialogListeners"/>
        </v-card>
      </v-dialog>

      <!-- Fora da tela cheia, os mesmos modais moram aqui. Um dos dois monta, nunca os
           dois: não dá para clicar no diagrama da página com a tela cheia aberta. -->
      <ontos-design-dialogs v-if="!expanded" v-bind="designDialogs" v-on="designDialogListeners"/>

      <!-- Fora do DesignDialogs: aquele reúne os destinos de um CLIQUE no diagrama, e
           estes vêm dos botões dos cabeçalhos, que a tela cheia nem mostra. -->
      <ontos-class-dialog v-if="creatingIdea" ref="classDialog"
                          :workspace="workspace" :saving="savingDesign"
                          @create="createCollection($event)"
                          @closed="creatingIdea = false"/>
      <!-- Um diálogo só para os dois gatilhos da seção de fluxos: sem `workflow` ele
           nasce criando, com ele nasce no design daquele fluxo. -->
      <ontos-workflow-dialog v-if="creatingWorkflow || editingWorkflow"
                             :workspace="workspace" :workflow="editingWorkflow" :saving="savingDesign"
                             @create="createWorkflowFromDesign($event)"
                             @save="saveEditedWorkflow($event)"
                             @closed="closeWorkflowDialog()"/>
      <!-- Mesmo par do fluxo: sem `domain` nasce criando, com ele nasce editando aquele. -->
      <ontos-domain-dialog v-if="creatingDomain || editingDomain"
                           :workspace="workspace" :domain="editingDomain" :saving="savingDesign"
                           @create="createDomainFromDesign($event)"
                           @save="saveEditedDomain($event)"
                           @closed="closeDomainDialog()"/>
      <ontos-roles-dialog v-if="editingRoles" :roles="workspace.roles || []" :workspace-title="workspace.title"
                          :saving="savingDesign"
                          @save="saveRolesFromDesign($event)" @closed="editingRoles = false"/>
    </div>
  </ontos-base>
</template>
<style scoped>
/* Em tela cheia o diagrama ocupa o que SOBRA do cabeçalho, e não uma altura descontada
   à mão: o desconto fixo errava assim que o cabeçalho quebrava em duas linhas, e somado
   ao padding do card estourava a janela — era o que empurrava tudo para os cantos.

   min-height: 0 em cada nível porque o padrão de um item flex é não encolher além do
   conteúdo; sem isso a viewport cresceria e voltaria a transbordar. */
.expanded-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.expanded-body {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
}

.expanded-diagram {
  flex: 1 1 auto;
  min-height: 0;
}

/* O :deep() alcança a viewport porque o estilo do filho é scoped. */
.expanded-diagram :deep(.class-diagram-viewport) {
  height: auto;
  flex: 1 1 auto;
  min-height: 0;
}
</style>
